import React from 'react';
import PropTypes from 'prop-types';

import {
  categories,
  audienceTitles,
  extractAudiences,
  linkWithQueryParams,
  queryParamsFromFilters,
} from 'inside-out/managers/resourceLibraryManager';

import { idToLink } from 'inside-out/managers/navManager';
import { Link } from 'inside-out/components/shared/link';

import { Cards } from 'inside-out/components/shared/cards';
import { ELearningCard } from 'inside-out/components/shared/cards/eLearningCard';
import { ResourceCard } from 'inside-out/components/shared/cards/resourceCard';
import { InPagePrompt } from 'inside-out/components/shared/inPagePrompt';

import { sectionStyle, subTitleStyle } from 'inside-out/page-styles/resource-library';
import ArrowIcon from 'inside-out/components/shared/icons/arrowIcon';

const resourcePropType = PropTypes.shape({
  edges: PropTypes.arrayOf(
    PropTypes.shape({
      node: PropTypes.shape({
        id: PropTypes.string,
        title: PropTypes.string,
        date: PropTypes.string,
        fields: PropTypes.shape({
          thumbnail_image: PropTypes.shape({
            childImageSharp: PropTypes.shape({
              resize: PropTypes.shape({
                src: PropTypes.string.isRequired,
              }).isRequired,
            }).isRequired,
          }).isRequired,
        }),
        attachments: PropTypes.arrayOf(
          PropTypes.shape({
            vimeo_url: PropTypes.string,
          })
        ),
      }).isRequired,
    }).isRequired
  ),
});

const ELearnings = ({data, onViewAll}) => (
  <section className={sectionStyle}>
    <header>
      <h1 className={subTitleStyle}>eLearning</h1>
      <Link
        to={linkWithQueryParams(queryParamsFromFilters({ audience: 'professionals', type: 'elearning' }))}
        onClick={() => onViewAll({ audience: 'professionals', type: 'elearning' })}>
        View all <ArrowIcon fill="inherit"/>
      </Link>
    </header>
    <Cards columns={1} scrollable={false}>
      { data.edges.map(({ node }, index) => (
        <ELearningCard
          to={idToLink(node.id)}
          key={index}
          fullWidth={true}
          hasVideo={!!node.attachments.find(a => a.vimeo_url)}
          mediaImage={node.fields && node.fields.thumbnail_image.childImageSharp.resize.src}
          title={node.title}
          body={node.extract}
          linkText={node.tile_button_text || 'Watch the Demo'}/>
      ))}
    </Cards>
  </section>
);
ELearnings.propTypes = {
  data: resourcePropType.isRequired,
  onViewAll: PropTypes.func.isRequired,
};

const AudienceSection = ({data, audience, onViewAll}) => {
  if (data == null || !Array.isArray(data.edges) || data.edges.length < 1) return null;
  return (
    <section className={sectionStyle}>
      <header>
        <h1 className={subTitleStyle}>{audienceTitles[audience]}</h1>
        <Link
          to={linkWithQueryParams(queryParamsFromFilters({ audience, type: categories[audience][0] }))}
          onClick={() => onViewAll({ audience, type: categories[audience][0] })}>
          View all <ArrowIcon fill="inherit"/>
        </Link>
      </header>
      <Cards columns={4}>
        {data.edges.map(({ node }, index) => (
          <ResourceCard
            key={index}
            id={node.id}
            to={idToLink(node.id)}
            background={node.fields && node.fields.thumbnail_image.childImageSharp.resize.src}
            audiences={extractAudiences(node.categories)}
            hasVideo={!!node.attachments.find(a => a.vimeo_url)}
            title={node.title}
            body={node.extract}
            date={node.date}/>
        ))}
      </Cards>
    </section>
  );
}
AudienceSection.propTypes = {
  data: resourcePropType.isRequired,
  audience: PropTypes.string.isRequired,
  onViewAll: PropTypes.func.isRequired,
};


export const LandingBlock = ({ data, page, onSectionSelect }) => {
  const {
    recentELearnings,
    recentForMe,
    recentForCarers,
    recentForResearchers,
    recentForServiceDevelopment,
  } = data;
  const pageSettings = page.edges[0].node;
  return (
    <React.Fragment>
      { recentELearnings && <ELearnings data={recentELearnings} onViewAll={onSectionSelect}/> }

      { recentForMe && <AudienceSection data={recentForMe} audience='me' onViewAll={onSectionSelect} /> }
      { recentForCarers && <AudienceSection data={recentForCarers} audience='carers' onViewAll={onSectionSelect} /> }

      <InPagePrompt
        topText={ pageSettings.purple_heading }
        bottomText={ pageSettings.purple_body }
        cta={{ to: pageSettings.button_to, text: pageSettings.button_text, }}
        inline={true}/>

      { recentForResearchers && <AudienceSection data={recentForResearchers} audience='researchers' onViewAll={onSectionSelect} /> }
      { recentForServiceDevelopment &&
        <AudienceSection data={recentForServiceDevelopment} audience='service_development' onViewAll={onSectionSelect} />
      }
    </React.Fragment>
  );
}

LandingBlock.propTypes = {
  data: PropTypes.shape({
    recentELearnings: resourcePropType.isRequired,
    recentForMe: resourcePropType.isRequired,
    recentForCarers: resourcePropType.isRequired,
    recentForResearchers: resourcePropType.isRequired,
    recentForServiceDevelopment: resourcePropType,
  }).isRequired,
  page: PropTypes.shape({
    edges: PropTypes.arrayOf(
      PropTypes.shape({
        node: PropTypes.shape({
          purple_heading: PropTypes.string.isRequired,
          purple_body: PropTypes.string.isRequired,
          button_text: PropTypes.string.isRequired,
          button_to: PropTypes.string.isRequired,
        }).isRequired
      })
    ).isRequired
  }).isRequired,
  onSectionSelect: PropTypes.func,
};



// WEBPACK FOOTER //
// ./src/components/resource-library/landingBlock/component.js